import { ButtonFooterLayout, ContinueButton, RoutingFlowTitle, StepLayout } from "#/components/carrot-plans/shared"
import {
  useCheckRoutingInfoQueryCache,
  useUpdateRoutingInfo
} from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { Form } from "@carrotfertility/carotene-core-x"
import React, { useCallback, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { FormRadioButtonGroup } from "#/services/common-forms/form-radio-button-group"
import { ChangeJourneyButton } from "#/components/carrot-plans/shared/ChangeJourneyButtton"
import {
  getLabelForJourneyStage,
  journeyStages,
  isPregnancyTrimesterStage,
  isPostpartumStage
} from "#/components/carrot-plans/hooks/useJourneyStageOptions"
import { getStateMachineValue } from "#/components/carrot-plans/steps/journey/WhereAreYouInJourneyStep"
import { useGetMostRecentlyCompletedRouting } from "#/components/carrot-plans/hooks/useGetMostRecentlyCompletedRouting"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { Journey } from "#/components/carrot-plans/types/journey"
import { JourneyStage } from "#/components/carrot-plans/types/journeyStage"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"
import { useGetRegistrationJourney } from "#/components/carrot-plans/hooks/useGetRegistrationJourney"

function lowercaseFirstLetter(string: string, locale: string) {
  if (typeof string !== "string" || string.length === 0) {
    return string
  }
  return string.charAt(0).toLocaleLowerCase(locale) + string.slice(1)
}

interface AreYouStillInThisPhaseContentProps {
  title: string
  options: Array<{ label: string; value: string }>
  defaultValue?: string
  onClickContinue: (formValues: { journeyStageCheckIn: "YES" | "NO" }) => Promise<void>
}

function AreYouStillInThisPhaseContent({
  title,
  options,
  defaultValue,
  onClickContinue
}: AreYouStillInThisPhaseContentProps) {
  const intl = useIntl()
  const theme = useTheme()

  return (
    <>
      <RoutingFlowTitle title={title} />
      <StepLayout
        header={<span id="areYouStillInThisPhase-label">{title}</span>}
        description={null}
        formElements={
          <>
            <ChangeJourneyButton />
            <Form onSubmit={onClickContinue} defaultValues={{ journeyStageCheckIn: defaultValue }}>
              <Stack spacing={theme.spacing(theme.tokens.spacing.sm)}>
                <FormRadioButtonGroup
                  name="journeyStageCheckIn"
                  options={options}
                  controllerProps={{ rules: { required: intl.formatMessage({ defaultMessage: "Required" }) } }}
                  aria-labelledby="areYouStillInThisPhase-label"
                />
              </Stack>
              <ButtonFooterLayout
                continueButton={
                  <ContinueButton>
                    <FormattedMessage defaultMessage="Continue" />
                  </ContinueButton>
                }
              />
            </Form>
          </>
        }
      />
    </>
  )
}

/**
 * This step is for members who added a journey stage during registration
 */
export function AreYouStillInThisPhaseStep() {
  const intl = useIntl()
  const registrationJourneyQuery = useGetRegistrationJourney()
  const registrationJourneyStage = registrationJourneyQuery?.data?.journeyStage
  const registrationOtherJourneyStage = registrationJourneyQuery?.data?.otherJourneyStage
  const registrationDueDate = registrationJourneyQuery?.data?.dueDate
  const registrationDeliveryDate = registrationJourneyQuery?.data?.deliveryDate

  const { send } = useStateMachine()
  const updateRoutingInfo = useUpdateRoutingInfo()

  const options = [
    { label: intl.formatMessage({ defaultMessage: "Yes" }), value: "YES" },
    { label: intl.formatMessage({ defaultMessage: "No, I've moved on" }), value: "NO" }
  ]

  const setRoutingJourneyStage = useCallback(async (): Promise<void> => {
    const payload: {
      journeyStage: string
      otherJourneyStage: string
      dueDate?: string
      deliveryDate?: string
    } = {
      journeyStage: registrationJourneyStage,
      otherJourneyStage: registrationOtherJourneyStage
    }

    if (isPregnancyTrimesterStage(registrationJourneyStage) && registrationDueDate) {
      payload.dueDate = registrationDueDate
    }

    if (isPostpartumStage(registrationJourneyStage) && registrationDeliveryDate) {
      payload.deliveryDate = registrationDeliveryDate
    }

    await updateRoutingInfo.mutateAsync(payload, {
      onSuccess: () => {
        send(getStateMachineValue(registrationJourneyStage))
      }
    })
  }, [
    send,
    updateRoutingInfo,
    registrationJourneyStage,
    registrationOtherJourneyStage,
    registrationDueDate,
    registrationDeliveryDate
  ])

  async function onClickContinue(formValues: { journeyStageCheckIn: "YES" | "NO" }): Promise<void> {
    if (formValues.journeyStageCheckIn === "YES") {
      await setRoutingJourneyStage()
    } else {
      send("NO")
    }
  }

  const formattedJourneyStage = registrationJourneyStage
    ? intl.formatMessage(getLabelForJourneyStage(registrationJourneyStage))
    : ""

  const title = intl.formatMessage(
    {
      defaultMessage: "Are you still in the “{journeyStage}” phase of your journey?"
    },
    {
      journeyStage: lowercaseFirstLetter(formattedJourneyStage, intl.locale)
    }
  )

  return registrationJourneyQuery.isLoading ? (
    <CenteredLoadingIndicator />
  ) : (
    <AreYouStillInThisPhaseContent title={title} options={options} onClickContinue={onClickContinue} />
  )
}

/**
 * This step is for members who enter the check-in experience
 */
export function AreYouStillInThisPhaseCheckinStep() {
  const intl = useIntl()
  const updateRoutingInfo = useUpdateRoutingInfo()
  // Previous journey data is used to populate the form if the user is still in the same journey stage
  const mostRecentlyCompletedRoutingQuery = useGetMostRecentlyCompletedRouting()
  const memberJourneyQuery = useGetMemberJourneyData()
  const prevJourneyData = memberJourneyQuery.data?.currentMemberJourney.withSubmittedRouting
  const prevJourney = prevJourneyData?.journey
  const previousJourneyStage = prevJourneyData?.journeyStage
  const previousOtherJourneyStage =
    previousJourneyStage === JourneyStage.OTHER_WITH_USER_INPUT
      ? memberJourneyQuery.data?.mostRecentSubmittedRoutingJourney.otherJourneyStage
      : null
  const previousPreservationInterest =
    prevJourney === Journey.PRESERVATION ? mostRecentlyCompletedRoutingQuery.data?.preservationInterest : null
  // InProgress routing info is used to populate the default value
  const routingInfo = useCheckRoutingInfoQueryCache()
  const defaultValue = routingInfo?.isStillInPrevJourneyStage
  const isOtherWithUserInput = routingInfo?.journeyStage === JourneyStage.OTHER_WITH_USER_INPUT
  const hasAnswered = !!routingInfo?.journey && !!defaultValue
  const { send } = useStateMachine(hasAnswered ? (isOtherWithUserInput ? "OTHER_WITH_USER_INPUT" : defaultValue) : null)
  const isLoading = mostRecentlyCompletedRoutingQuery.isLoading && memberJourneyQuery.isLoading
  const isJourneyStageMissingOrUnsupported = !journeyStages.some((j) => j.value === previousJourneyStage)
  // Skip step if no valid journey data to populate content
  useEffect(() => {
    if (!isLoading) {
      if (prevJourney === Journey.SOMETHING_ELSE) {
        send("SOMETHING_ELSE")
      } else if (isJourneyStageMissingOrUnsupported) {
        send("NO_PHASE")
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const options = [
    { label: intl.formatMessage({ defaultMessage: "Yes" }), value: "YES" },
    { label: intl.formatMessage({ defaultMessage: "No, I've moved on" }), value: "NO" },
    { label: intl.formatMessage({ defaultMessage: "My journey is not going as planned" }), value: "SENSITIVE_SCENARIO" }
  ]

  async function onClickContinue(formValues: {
    journeyStageCheckIn: "YES" | "NO" | "SENSITIVE_SCENARIO"
  }): Promise<void> {
    if (formValues.journeyStageCheckIn === "YES" || formValues.journeyStageCheckIn === "SENSITIVE_SCENARIO") {
      await updateRoutingInfo.mutateAsync(
        {
          journey: prevJourney,
          journeyStage: previousJourneyStage,
          otherJourneyStage: previousOtherJourneyStage,
          preservationInterest: previousPreservationInterest,
          isStillInPrevJourneyStage: formValues.journeyStageCheckIn
        },
        {
          onSuccess: () => {
            if (previousJourneyStage === JourneyStage.OTHER_WITH_USER_INPUT) {
              send("OTHER_WITH_USER_INPUT")
            } else {
              send(formValues.journeyStageCheckIn)
            }
          }
        }
      )
    } else if (formValues.journeyStageCheckIn === "NO") {
      // Answer is No, still set the journey with the isStillInPrevJourneyStage answer
      await updateRoutingInfo.mutateAsync(
        {
          journey: prevJourney,
          isStillInPrevJourneyStage: formValues.journeyStageCheckIn
        },
        {
          onSuccess: () => {
            send(formValues.journeyStageCheckIn)
          }
        }
      )
    }
  }

  const formattedJourneyStage = isJourneyStageMissingOrUnsupported
    ? ""
    : intl.formatMessage(getLabelForJourneyStage(previousJourneyStage))

  const title = intl.formatMessage(
    {
      defaultMessage: "Are you still in the “{journeyStage}” phase of your journey?"
    },
    {
      journeyStage: lowercaseFirstLetter(formattedJourneyStage, intl.locale)
    }
  )

  if (isLoading) {
    return <CenteredLoadingIndicator />
  }

  return (
    <AreYouStillInThisPhaseContent
      title={title}
      options={options}
      onClickContinue={onClickContinue}
      defaultValue={defaultValue}
    />
  )
}
